import {
  partialMatchKey,
  type MutationFunctionContext,
  type QueryClient,
} from '@tanstack/react-query';
import { Effect } from 'effect';
import { audioItemsBaseKey } from '../audio-items';
import {
  type FolderDetailRow,
  folderDetailQueryKey,
  type FolderRow,
  foldersQueryKey,
  foldersRootQueryKey,
  updateFolderEffect,
} from '../folders';
import { type AppRuntime, useAppRuntime } from '../runtime-context';
import {
  folderAudiosQueryKey,
  folderPickerQueryKey,
  unfiledAudiosQueryKey,
} from './audio-list-hooks';
import {
  applyAudioCacheOp,
  applyOpToData,
  cancelAudioCaches,
  invalidateAudioCaches,
  restoreAudioCacheSnapshot,
  type WalkSnapshot,
} from './walk-audio-caches';

// Cross-cache folder WRITE lifecycles, shared across apps (start / mobile /
// browser-ext) — the multi-cache counterpart to the single-cache
// `optimistic-list-entity` helper and a sibling of `walk-audio-caches`'
// `build*/use*` pair. Moving an audio between folders touches BOTH the
// `['audioItems']` caches (the row's `folder_id` + folder-list membership) and
// the `['folders']` caches (list counts + each folder-detail's id array), so it
// can't ride the single-cache helper — but the choreography is entirely
// app-agnostic and leans only on shared primitives. Each app keeps only its own
// `onSuccess` (toast / navigate / analytics) and supplies the runtime via
// `AppRuntimeProvider`.
//
// Pattern mirrors `buildAudioWalkLifecycle` / `useAudioWalkLifecycle`: a pure
// `build*({ runtime })` factory (unit-testable without React) plus a `use*`
// hook that injects `useAppRuntime()`.
//
// See docs/projects/complete/web/shared-tanstack-query.md.

// What the move needs from an audio row: its id + current folder. Kept
// structural (not `AudioItemSummary`) so any app's row type satisfies it.
export type MoveAudioToFolderInput = {
  folderId: string;
  audioItem: { id: string; folder_id?: string | null };
};

export const moveAudioToFolderEffect = (opts: MoveAudioToFolderInput) =>
  updateFolderEffect(opts.folderId, {
    audio_conversion_ids: [opts.audioItem.id],
  });

type MoveContext = {
  audioSnapshot: WalkSnapshot;
  srcDetailSnap: FolderDetailRow | undefined;
  destDetailSnap: FolderDetailRow | undefined;
  srcFolderId: string;
  destFolderId: string;
};

const restoreMove = (qc: QueryClient, moveContext: MoveContext | undefined) =>
  Effect.sync(() => {
    if (!moveContext) return;
    restoreAudioCacheSnapshot(qc, moveContext.audioSnapshot);
    qc.setQueryData(
      folderDetailQueryKey(moveContext.srcFolderId),
      moveContext.srcDetailSnap,
    );
    qc.setQueryData(
      folderDetailQueryKey(moveContext.destFolderId),
      moveContext.destDetailSnap,
    );
  });

const settleMove = (qc: QueryClient) =>
  Effect.gen(function* () {
    yield* Effect.promise(() => invalidateAudioCaches(qc));
    yield* Effect.promise(() =>
      qc.invalidateQueries({ queryKey: foldersRootQueryKey }),
    );
  });

type MoveLifecycle = {
  onMutate: (
    v: MoveAudioToFolderInput,
    ctx: MutationFunctionContext,
  ) => Promise<MoveContext>;
  onError: (
    err: unknown,
    v: MoveAudioToFolderInput,
    onMutateResult: MoveContext | undefined,
    ctx: MutationFunctionContext,
  ) => Promise<void>;
  onSettled: (
    data: unknown,
    err: unknown,
    v: MoveAudioToFolderInput,
    onMutateResult: unknown,
    ctx: MutationFunctionContext,
  ) => Promise<void>;
};

// Optimistic in onMutate for an instant move; onSettled invalidates the
// `['audioItems']` + `['folders']` prefixes so the server reconciles list
// membership.
//   onMutate  — patch `folder_id` across every audio cache (the walk), REMOVE
//               the row from the src folder's audio list, and update src/dest
//               folder-detail `audio_conversion_ids`. Snapshot every write.
//   onError   — restore all snapshots.
//   onSettled — invalidate `['audioItems']` + `['folders']`.
const moveAudioInCaches = (qc: QueryClient, v: MoveAudioToFolderInput) =>
  Effect.gen(function* () {
    const audioId = v.audioItem.id;
    const srcFolderId = v.audioItem.folder_id ?? 'root';
    const destFolderId = v.folderId;
    const nextFolderId = destFolderId === 'root' ? null : destFolderId;
    const srcAudioListKeys =
      srcFolderId === 'root'
        ? [folderAudiosQueryKey('root'), unfiledAudiosQueryKey()]
        : [folderAudiosQueryKey(srcFolderId)];

    // Every cache the optimistic writes below touch, not just the audio tree:
    // a folder-detail read landing after onMutate restores the pre-move
    // membership and the row snaps back to where it was dragged from.
    yield* Effect.all(
      [
        Effect.promise(() => cancelAudioCaches(qc)),
        Effect.promise(() => qc.cancelQueries({ queryKey: foldersQueryKey })),
        Effect.promise(() =>
          qc.cancelQueries({ queryKey: folderDetailQueryKey(srcFolderId) }),
        ),
        Effect.promise(() =>
          qc.cancelQueries({ queryKey: folderDetailQueryKey(destFolderId) }),
        ),
      ],
      { concurrency: 'unbounded' },
    );

    const srcDetailSnap = qc.getQueryData<FolderDetailRow>(
      folderDetailQueryKey(srcFolderId),
    );
    const destDetailSnap = qc.getQueryData<FolderDetailRow>(
      folderDetailQueryKey(destFolderId),
    );

    // 1. Flip folder_id across every audio cache.
    const audioSnapshot: WalkSnapshot = applyAudioCacheOp(qc, {
      kind: 'patchFields',
      id: audioId,
      partial: { folder_id: nextFolderId },
    });

    // 2. Remove from the src folder's audio list (it no longer belongs).
    //    The dest folder's list is invalidated on settle to reconcile.
    for (const srcAudioListKey of srcAudioListKeys) {
      const srcAudioList = qc.getQueryData(srcAudioListKey);
      if (srcAudioList !== undefined) {
        qc.setQueryData(
          srcAudioListKey,
          applyOpToData(srcAudioList, { kind: 'removeById', id: audioId }),
        );
      }
    }

    // 3. Folder details: src loses the id, dest gains it.
    qc.setQueryData<FolderDetailRow>(
      folderDetailQueryKey(srcFolderId),
      (old) =>
        old
          ? {
              ...old,
              audio_conversion_ids: old.audio_conversion_ids.filter(
                (x) => x !== audioId,
              ),
            }
          : old,
    );
    qc.setQueryData<FolderDetailRow>(
      folderDetailQueryKey(destFolderId),
      (old) =>
        old && !old.audio_conversion_ids.includes(audioId)
          ? {
              ...old,
              audio_conversion_ids: [...old.audio_conversion_ids, audioId],
            }
          : old,
    );

    return {
      audioSnapshot,
      srcDetailSnap,
      destDetailSnap,
      srcFolderId,
      destFolderId,
    };
  });

export function buildMoveAudioToFolderLifecycle(opts: {
  runtime: AppRuntime;
}): MoveLifecycle {
  const { runtime } = opts;
  return {
    onMutate: (v, ctx) => runtime.runPromise(moveAudioInCaches(ctx.client, v)),

    onError: (_err, _v, onMutateResult, ctx) =>
      runtime.runPromise(restoreMove(ctx.client, onMutateResult)),

    onSettled: (_d, _e, _v, _r, ctx) =>
      runtime.runPromise(settleMove(ctx.client)),
  };
}

export function useMoveAudioToFolderLifecycle(): MoveLifecycle {
  return buildMoveAudioToFolderLifecycle({ runtime: useAppRuntime() });
}

export type AddAudiosToFolderInput = {
  folderId: string;
  audioIds: ReadonlyArray<string>;
};

export const addAudiosToFolderEffect = (input: AddAudiosToFolderInput) =>
  updateFolderEffect(input.folderId, {
    audio_conversion_ids: [...input.audioIds],
  });

export const reconcileFolderAssignment = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: audioItemsBaseKey,
      predicate: (query) =>
        !partialMatchKey(query.queryKey, folderPickerQueryKey),
    }),
    queryClient.invalidateQueries({ queryKey: foldersRootQueryKey }),
  ]);

type FolderPickerPages = {
  pages: ReadonlyArray<{
    items: ReadonlyArray<{ id: string; folder_id: string | null }>;
  }>;
};

/** The picker keeps its rows across an add (it is excluded from the
 * invalidation above), so the added ids are marked as filed in place. */
export const markAudiosFiled = (
  queryClient: QueryClient,
  folderId: string,
  audioIds: ReadonlyArray<string>,
) => {
  const filed = new Set(audioIds);
  queryClient.setQueriesData<FolderPickerPages>(
    { queryKey: folderPickerQueryKey },
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.map((item) =>
            filed.has(item.id) ? { ...item, folder_id: folderId } : item,
          ),
        })),
      },
  );
};

// ===========================================================================
// Rename folder (path) — dual-cache write
// ===========================================================================
//
// Renaming touches BOTH the folders LIST cache (drives the /files index) and
// the per-folder DETAIL cache (drives the name shown on the folder route). We
// deliberately do NOT route through the single-cache `optimistic-list-entity`
// helper: its `update()` throws when the row isn't in the list cache (cold on a
// direct folder-route load) and it can't touch the detail cache. Writing both
// caches directly (each a no-op on a cache miss, so the PATCH always fires) +
// invalidating both on settle fixes both.

export type RenameFolderInput = { id: string; path: ReadonlyArray<string> };

// mutationFn body: PATCH the folder's path.
export const renameFolderEffect = (input: RenameFolderInput) =>
  updateFolderEffect(input.id, { path: [...input.path] });

type RenameContext = {
  listSnap: ReadonlyArray<FolderRow> | undefined;
  detailSnap: FolderDetailRow | undefined;
};

type RenameLifecycle = {
  onMutate: (
    v: RenameFolderInput,
    ctx: MutationFunctionContext,
  ) => Promise<RenameContext>;
  onError: (
    err: unknown,
    v: RenameFolderInput,
    onMutateResult: RenameContext | undefined,
    ctx: MutationFunctionContext,
  ) => Promise<void>;
  onSettled: (
    data: unknown,
    err: unknown,
    v: RenameFolderInput,
    onMutateResult: unknown,
    ctx: MutationFunctionContext,
  ) => Promise<void>;
};

export function buildRenameFolderLifecycle(opts: {
  runtime: AppRuntime;
}): RenameLifecycle {
  const { runtime } = opts;
  return {
    onMutate: (v, ctx) =>
      runtime.runPromise(
        Effect.gen(function* () {
          const qc = ctx.client;
          const detailKey = folderDetailQueryKey(v.id);
          yield* Effect.promise(() =>
            qc.cancelQueries({ queryKey: foldersQueryKey }),
          );
          yield* Effect.promise(() =>
            qc.cancelQueries({ queryKey: detailKey }),
          );

          const listSnap =
            qc.getQueryData<ReadonlyArray<FolderRow>>(foldersQueryKey);
          const detailSnap = qc.getQueryData<FolderDetailRow>(detailKey);
          const nextPath = [...v.path];

          // Both writes are no-ops when the row is absent (cold cache); the
          // PATCH fires regardless, so a missing row never blocks the rename.
          qc.setQueryData<ReadonlyArray<FolderRow>>(foldersQueryKey, (rows) =>
            rows?.map((f) => (f.id === v.id ? { ...f, path: nextPath } : f)),
          );
          qc.setQueryData<FolderDetailRow>(detailKey, (old) =>
            old ? { ...old, path: nextPath } : old,
          );

          return { listSnap, detailSnap };
        }),
      ),

    onError: (_err, v, onMutateResult, ctx) =>
      runtime.runPromise(
        Effect.sync(() => {
          if (!onMutateResult) return;
          const qc = ctx.client;
          if (onMutateResult.listSnap !== undefined) {
            qc.setQueryData(foldersQueryKey, onMutateResult.listSnap);
          }
          qc.setQueryData(
            folderDetailQueryKey(v.id),
            onMutateResult.detailSnap,
          );
        }),
      ),

    onSettled: (_d, _e, v, _r, ctx) =>
      runtime.runPromise(
        Effect.gen(function* () {
          yield* Effect.promise(() =>
            ctx.client.invalidateQueries({ queryKey: foldersQueryKey }),
          );
          yield* Effect.promise(() =>
            ctx.client.invalidateQueries({
              queryKey: folderDetailQueryKey(v.id),
            }),
          );
        }),
      ),
  };
}

export function useRenameFolderLifecycle(): RenameLifecycle {
  return buildRenameFolderLifecycle({ runtime: useAppRuntime() });
}
